/**
 * Relay Service Utility
 * Handles communication relay between buyers and sellers
 * without exposing personal contact information
 */

import { supabase } from '../api/supabase';

/**
 * Creates a new relay channel for communication between a buyer and seller
 * @param {string} buyerId - The ID of the buyer
 * @param {string} sellerId - The ID of the seller
 * @param {string} leadId - The ID of the lead
 * @returns {Promise<Object>} The created relay channel
 */
export const createRelayChannel = async (buyerId, sellerId, leadId) => {
  try {
    // Generate a unique relay ID
    const relayId = `relay_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    
    const { data, error } = await supabase
      .from('relay_channels')
      .insert([
        {
          relay_id: relayId,
          buyer_id: buyerId,
          seller_id: sellerId,
          lead_id: leadId,
          status: 'active',
          created_at: new Date()
        }
      ])
      .select();
    
    if (error) throw error;
    
    return data[0];
  } catch (error) {
    console.error('Error creating relay channel:', error);
    throw error;
  }
};

/**
 * Sends an email through the relay system
 * @param {string} relayId - The ID of the relay channel
 * @param {string} senderId - The ID of the sender (buyer or seller)
 * @param {string} message - The message content
 * @param {string} subject - The email subject
 * @returns {Promise<Object>} The sent message
 */
export const sendRelayEmail = async (relayId, senderId, message, subject) => {
  try {
    // First, verify the relay channel exists and the sender is authorized
    const { data: relayData, error: relayError } = await supabase
      .from('relay_channels')
      .select('*')
      .eq('relay_id', relayId)
      .single();
    
    if (relayError) throw relayError;
    
    if (relayData.buyer_id !== senderId && relayData.seller_id !== senderId) {
      throw new Error('Unauthorized sender for this relay channel');
    }
    
    // Determine if sender is buyer or seller
    const isBuyer = relayData.buyer_id === senderId;
    const recipientId = isBuyer ? relayData.seller_id : relayData.buyer_id;
    
    // Store the message in the database
    const { data: messageData, error: messageError } = await supabase
      .from('relay_messages')
      .insert([
        {
          relay_id: relayId,
          sender_id: senderId,
          recipient_id: recipientId,
          message_type: 'email',
          subject,
          content: message,
          status: 'sent',
          created_at: new Date()
        }
      ])
      .select();
    
    if (messageError) throw messageError;
    
    // In a real implementation, this would trigger a serverless function
    // to send the actual email through a service like SendGrid or AWS SES
    // For now, we'll just return the message data
    
    return messageData[0];
  } catch (error) {
    console.error('Error sending relay email:', error);
    throw error;
  }
};

/**
 * Sends an SMS through the relay system
 * @param {string} relayId - The ID of the relay channel
 * @param {string} senderId - The ID of the sender (buyer or seller)
 * @param {string} message - The message content
 * @returns {Promise<Object>} The sent message
 */
export const sendRelaySMS = async (relayId, senderId, message) => {
  try {
    // First, verify the relay channel exists and the sender is authorized
    const { data: relayData, error: relayError } = await supabase
      .from('relay_channels')
      .select('*')
      .eq('relay_id', relayId)
      .single();
    
    if (relayError) throw relayError;
    
    if (relayData.buyer_id !== senderId && relayData.seller_id !== senderId) {
      throw new Error('Unauthorized sender for this relay channel');
    }
    
    // Determine if sender is buyer or seller
    const isBuyer = relayData.buyer_id === senderId;
    const recipientId = isBuyer ? relayData.seller_id : relayData.buyer_id;
    
    // Store the message in the database
    const { data: messageData, error: messageError } = await supabase
      .from('relay_messages')
      .insert([
        {
          relay_id: relayId,
          sender_id: senderId,
          recipient_id: recipientId,
          message_type: 'sms',
          content: message,
          status: 'sent',
          created_at: new Date()
        }
      ])
      .select();
    
    if (messageError) throw messageError;
    
    // In a real implementation, this would trigger a serverless function
    // to send the actual SMS through a service like Twilio
    // For now, we'll just return the message data
    
    return messageData[0];
  } catch (error) {
    console.error('Error sending relay SMS:', error);
    throw error;
  }
};

/**
 * Gets all messages for a specific relay channel
 * @param {string} relayId - The ID of the relay channel
 * @param {string} userId - The ID of the user requesting messages
 * @returns {Promise<Array>} Array of messages
 */
export const getRelayMessages = async (relayId, userId) => {
  try {
    // First, verify the relay channel exists and the user is authorized
    const { data: relayData, error: relayError } = await supabase
      .from('relay_channels')
      .select('*')
      .eq('relay_id', relayId)
      .single();
    
    if (relayError) throw relayError;
    
    if (relayData.buyer_id !== userId && relayData.seller_id !== userId) {
      throw new Error('Unauthorized user for this relay channel');
    }
    
    // Get all messages for this relay channel
    const { data: messages, error: messagesError } = await supabase
      .from('relay_messages')
      .select('*')
      .eq('relay_id', relayId)
      .order('created_at', { ascending: true });
    
    if (messagesError) throw messagesError;
    
    return messages;
  } catch (error) {
    console.error('Error getting relay messages:', error);
    throw error;
  }
};

export default {
  createRelayChannel,
  sendRelayEmail,
  sendRelaySMS,
  getRelayMessages
}; 